/**
 * Maintenova Rule-Based Chat Engine
 *
 * Architecture: pattern-matched rules with keyword scoring + fallback.
 * To swap in an AI API later, replace getResponse() with an async API call
 * and keep the same { text, suggestions } return shape.
 */

const RULES = [
  // ── Greetings ──
  {
    patterns: [/^(hi|hello|hey|howdy|greetings|good\s*(morning|afternoon|evening))/i],
    keywords: [],
    response: {
      text: "Hello! Welcome to Maintenova. I'm here to help you with questions about our website maintenance and DevOps automation platform. What can I help you with?",
      suggestions: ['What does Maintenova do?', 'Show me pricing', 'How do I get started?'],
    },
  },

  // ── What is Maintenova ──
  {
    patterns: [/what\s*(is|does)\s*maintenova/i, /tell\s*me\s*about\s*(maintenova|your\s*platform)/i, /what\s*do\s*you\s*do/i],
    keywords: ['about', 'maintenova', 'platform', 'company'],
    response: {
      text: "Maintenova is a DevOps automation platform that handles website and server maintenance automatically. We run health checks, manage backups, monitor SSL certificates, handle security scanning, and provide a client portal — so you can stop SSH-ing into servers and automate everything.\n\nOur platform monitors 20+ metrics on every server, every hour, and auto-remediates common issues before they become outages.",
      suggestions: ['What features do you offer?', 'Show me pricing', 'How does monitoring work?'],
    },
  },

  // ── Features ──
  {
    patterns: [/features?/i, /what\s*(can|do)\s*you\s*(offer|provide|do)/i, /capabilities/i],
    keywords: ['feature', 'offer', 'provide', 'capability', 'include'],
    response: {
      text: "Here's what Maintenova includes:\n\n• **Automated Health Checks** — 20+ server metrics monitored hourly\n• **Backup Management** — Automated backups with offsite replication & restore testing\n• **SSL Certificate Monitoring** — Track expirations with 30/14/7-day alerts\n• **Security Scanning** — Vulnerability detection for CMS, dependencies & server config\n• **Zero-Downtime Deployments** — Atomic deploys with auto-rollback\n• **Support Tickets** — Built-in ticketing with SLA tracking\n• **Client Portal** — Branded dashboard for your clients\n• **Maintenance Reports** — Automated reports on all maintenance activity\n• **Custom Runbooks** — Automated remediation workflows\n• **Integrations** — Slack, PagerDuty, webhooks & more",
      suggestions: ['Tell me about pricing', 'How do backups work?', 'What about security?'],
    },
  },

  // ── Pricing ──
  {
    patterns: [/pric/i, /how\s*much/i, /cost/i, /plans?\b/i, /subscription/i, /packages?/i],
    keywords: ['price', 'cost', 'plan', 'subscription', 'package', 'pay', 'billing', 'afford', 'budget', 'money', 'dollar', 'monthly', 'annual'],
    response: {
      text: "We offer three plans:\n\n**Starter — $99/month**\nUp to 2 websites, daily backups, uptime monitoring, SSL management, weekly security scans, email support, 48-hour response time.\n\n**Professional — $249/month** ⭐ Most Popular\nUp to 10 websites, hourly backups, daily security scans, plugin updates, performance optimization, priority support, 12-hour response time, monthly reports.\n\n**Enterprise — Custom pricing**\nUnlimited websites, real-time backups, continuous monitoring, zero-downtime deployments, custom runbooks, dedicated account manager, 15-minute SLA.\n\nAll plans include a **14-day free trial** with no credit card required. Save 20% with annual billing!",
      suggestions: ['Start free trial', 'Compare plans', 'Contact sales'],
    },
  },

  // ── Free trial ──
  {
    patterns: [/free\s*trial/i, /try\s*(it|maintenova|for\s*free)/i, /demo/i],
    keywords: ['trial', 'free', 'try', 'demo', 'test'],
    response: {
      text: "Yes! We offer a **14-day free trial** on all plans — no credit card required. You'll get full access to every feature during the trial period.\n\nYou can sign up directly on our website and have your first server connected in under 5 minutes.",
      suggestions: ['How do I sign up?', 'What happens after the trial?', 'Show me pricing'],
    },
  },

  // ── Getting started / Sign up ──
  {
    patterns: [/get\s*started/i, /sign\s*up/i, /create\s*(an\s*)?account/i, /register/i, /onboard/i, /setup/i, /how\s*to\s*start/i],
    keywords: ['start', 'signup', 'register', 'account', 'create', 'onboard', 'setup', 'begin'],
    response: {
      text: "Getting started is easy:\n\n1. **Sign up** — Create your account (takes 30 seconds)\n2. **Add a project** — Enter your domain and server details\n3. **Connect your server** — Install our lightweight agent or provide SSH access\n4. **Configure alerts** — Choose where you want notifications (email, Slack, etc.)\n\nMaintenova automatically runs a full health check and generates a baseline report within minutes of connecting. You can sign up at the top of this page!",
      suggestions: ['What server access do you need?', 'Is it secure?', 'Show me pricing'],
    },
  },

  // ── Monitoring / Health checks ──
  {
    patterns: [/monitor/i, /health\s*check/i, /uptime/i, /server\s*(status|health)/i],
    keywords: ['monitor', 'health', 'uptime', 'check', 'status', 'alert', 'notification'],
    response: {
      text: "Maintenova runs **20+ automated health checks** on every server:\n\n**System:** CPU, memory, disk space, disk I/O, system load\n**Services:** Web server, database, queue workers, mail, cron jobs\n**Security:** SSL expiry, firewall rules, failed logins, file integrity\n**Application:** HTTP responses, DNS resolution, backup freshness\n\nCheck intervals range from 30 seconds (Enterprise) to 5 minutes (Starter). Failed checks trigger auto-remediation first, then engineer alerts if the issue persists.",
      suggestions: ['What happens when a check fails?', 'Tell me about alerts', 'What about backups?'],
    },
  },

  // ── Backups ──
  {
    patterns: [/backup/i, /restore/i, /data\s*(loss|protection|recovery)/i, /3-2-1/i],
    keywords: ['backup', 'restore', 'recovery', 'snapshot', 'replicate'],
    response: {
      text: "We follow the **3-2-1 backup strategy**:\n\n• **3 copies** of your data (original + 2 backups)\n• **2 different media** types\n• **1 offsite copy** for disaster recovery\n\n**Backup schedule:**\n— Hourly snapshots (retained 24 hours)\n— Daily full backups (retained 30 days)\n— Weekly archives (retained 90 days)\n\nAll offsite backups are **AES-256 encrypted**. We run **automated restore tests weekly** to verify backup integrity. You can also trigger on-demand backups and restores from your dashboard.",
      suggestions: ['How fast can you restore?', 'What about security?', 'Show me pricing'],
    },
  },

  // ── Security ──
  {
    patterns: [/secur/i, /vulnerabilit/i, /hack/i, /malware/i, /firewall/i, /ssl/i, /certificate/i],
    keywords: ['security', 'secure', 'vulnerability', 'hack', 'malware', 'firewall', 'ssl', 'certificate', 'encryption', 'protection', 'safe'],
    response: {
      text: "Security is core to what we do:\n\n• **SSL Certificate Management** — Auto-renewal, expiry alerts (30/14/7 days), chain validation\n• **Vulnerability Scanning** — CMS, plugin, and dependency CVE checks\n• **Malware Detection** — File-level scanning for known signatures\n• **Firewall Monitoring** — Unexpected port exposure detection\n• **Brute-Force Protection** — Failed login attempt monitoring\n• **File Integrity Monitoring** — Alerts on unauthorized core file changes\n• **Security Headers** — CSP, HSTS, X-Frame-Options verification\n\nAll data in transit uses TLS 1.3. Backups are encrypted with AES-256. Our platform uses key-based SSH authentication with minimal-privilege access.",
      suggestions: ['How do you handle SSL?', 'Tell me about backups', 'What about WordPress security?'],
    },
  },

  // ── Deployments ──
  {
    patterns: [/deploy/i, /release/i, /ship\s*(code|update)/i, /rollback/i, /zero[\s-]*downtime/i],
    keywords: ['deploy', 'deployment', 'release', 'rollback', 'downtime', 'ship'],
    response: {
      text: "Our **zero-downtime deployment engine** ensures your users never notice when you ship new code:\n\n• **Atomic symlink deployments** — New code goes into a timestamped directory, then we atomically switch the symlink\n• **Pre-deploy health checks** — Verify the new release passes all checks before switching traffic\n• **Auto-rollback** — If post-deploy checks fail, we revert within 30 seconds\n• **Deploy hooks** — Run custom scripts at any stage\n• **Git integration** — Deploy directly from GitHub, GitLab, or Bitbucket\n\nAvailable on Professional and Enterprise plans.",
      suggestions: ['How do database migrations work?', 'Show me pricing', 'What about rollbacks?'],
    },
  },

  // ── Support / Help ──
  {
    patterns: [/support/i, /help\s*(me|center)?/i, /contact/i, /reach\s*(out|you)/i, /talk\s*to\s*(a\s*)?(human|person|agent|someone)/i],
    keywords: ['support', 'help', 'contact', 'reach', 'agent', 'human', 'person', 'talk', 'chat', 'email', 'phone'],
    response: {
      text: "We're here to help! Here's how to reach us:\n\n📧 **Email:** hello@maintenova.com / support@maintenova.com\n⏱ **Response time:** Within 4 hours on business days\n🚨 **Emergency:** Enterprise clients get 24/7 on-call with a 15-minute SLA\n\nYou can also:\n• Visit our **Contact page** to send a message\n• Check our **Knowledge Base** for self-service guides\n• Use the **built-in ticketing system** once you have an account\n\nOur support team handles everything from server issues to billing questions.",
      suggestions: ['Go to Contact page', 'View Knowledge Base', 'Show me pricing'],
    },
  },

  // ── WordPress ──
  {
    patterns: [/wordpress/i, /wp[\s-]?(admin|login|plugin|theme)/i, /cms/i],
    keywords: ['wordpress', 'wp', 'cms', 'plugin', 'theme', 'woocommerce'],
    response: {
      text: "We have deep WordPress expertise. Here's what we handle for WordPress sites:\n\n• **Core, plugin & theme updates** — Applied within 48 hours of release\n• **Security hardening** — Custom login URLs, file editing disabled, correct permissions, WAF configuration\n• **Performance optimization** — Caching, database optimization, image compression\n• **Malware scanning** — Daily scans with automatic quarantine\n• **Brute-force protection** — Login attempt limiting and IP blocking\n\nWe manage WordPress, WooCommerce, Joomla, Drupal, and custom PHP/Node.js applications.",
      suggestions: ['What about security?', 'How do updates work?', 'Show me pricing'],
    },
  },

  // ── Integrations ──
  {
    patterns: [/integrat/i, /slack/i, /pagerduty/i, /webhook/i, /connect\s*with/i],
    keywords: ['integration', 'slack', 'pagerduty', 'webhook', 'api', 'connect'],
    response: {
      text: "Maintenova integrates with your existing tools:\n\n• **Slack** — Alerts in any channel, acknowledge incidents, run quick actions\n• **PagerDuty** — Auto-create incidents for critical alerts, auto-resolve when fixed\n• **Webhooks** — Send alert data to any URL for custom integrations\n• **Email** — Configurable alert routing by type and severity\n• **API** — Full REST API for custom automation and reporting\n\nAlert routing lets you send different alert types to different channels (e.g., security → #security, performance → #ops).",
      suggestions: ['Tell me about the API', 'What about monitoring?', 'Show me pricing'],
    },
  },

  // ── Client portal / White-label ──
  {
    patterns: [/client\s*portal/i, /white[\s-]*label/i, /brand/i, /agency/i, /resell/i],
    keywords: ['portal', 'client', 'whitelabel', 'brand', 'agency', 'resell', 'dashboard'],
    response: {
      text: "Our **client portal** lets you give your clients their own branded dashboard:\n\n• **Custom branding** — Your logo, colors, and domain\n• **Project visibility** — Clients see health scores, activity, and maintenance windows\n• **Ticket system** — Clients create, track, and comment on support tickets\n• **Billing** — Clients view invoices and update payment methods\n• **Reports** — Automated maintenance reports delivered to clients\n\nPerfect for agencies managing multiple client websites. Available on all plans.",
      suggestions: ['How does branding work?', 'Tell me about reports', 'Show me pricing'],
    },
  },

  // ── Cancellation ──
  {
    patterns: [/cancel/i, /refund/i, /stop\s*(service|subscription)/i, /money\s*back/i],
    keywords: ['cancel', 'refund', 'stop', 'terminate', 'end'],
    response: {
      text: "You can cancel your subscription at any time — **no long-term contracts, no cancellation fees**.\n\n• Cancel from your account settings\n• You'll retain access until the end of your current billing period\n• We'll help you export your data and transition smoothly\n• All your monitoring data and reports remain available for 30 days after cancellation\n\nIf you're having issues, we'd love the chance to help resolve them before you go!",
      suggestions: ['Contact support', 'Show me pricing', 'What features do you offer?'],
    },
  },

  // ── SLA / Uptime guarantee ──
  {
    patterns: [/sla/i, /uptime\s*guarantee/i, /service\s*level/i, /guarantee/i],
    keywords: ['sla', 'guarantee', 'uptime', 'availability'],
    response: {
      text: "We take reliability seriously:\n\n• **99.9% uptime guarantee** on our monitoring platform\n• **Enterprise SLA** — Custom SLAs with service credits for any breach\n• **15-minute response SLA** for critical issues (Enterprise)\n• **12-hour response time** for Professional plans\n• **48-hour response time** for Starter plans\n\nOur monitoring infrastructure runs across multiple cloud providers and regions to ensure we're always watching, even if one provider has issues.",
      suggestions: ['Show me Enterprise pricing', 'What about support?', 'Tell me about monitoring'],
    },
  },

  // ── Thank you / Goodbye ──
  {
    patterns: [/^(thanks?|thank\s*you|thx|ty|cheers)/i, /^(bye|goodbye|see\s*ya|later|good\s*night)/i],
    keywords: [],
    response: {
      text: "You're welcome! If you have any more questions, I'm always here. Feel free to explore our website or reach out to our team at hello@maintenova.com. Have a great day! 👋",
      suggestions: ['Show me pricing', 'Contact support', 'View Knowledge Base'],
    },
  },
];

const FALLBACK = {
  text: "I'm not sure I have the answer to that specific question, but I'd love to help! Here are some things I can assist with:\n\n• Our **features and capabilities**\n• **Pricing and plans**\n• **Getting started** with Maintenova\n• **Security and backups**\n• **Monitoring and health checks**\n• **Deployments and integrations**\n\nOr you can reach our support team at **hello@maintenova.com** for personalized help.",
  suggestions: ['What does Maintenova do?', 'Show me pricing', 'Contact support'],
};

/**
 * Score a message against a rule using pattern matches + keyword overlap.
 * Returns 0 if no match, higher = better match.
 */
function scoreRule(message, rule) {
  const lower = message.toLowerCase();

  // Check regex patterns first (high-confidence matches)
  for (const pattern of rule.patterns) {
    if (pattern.test(message)) return 100;
  }

  // Keyword scoring
  if (rule.keywords.length === 0) return 0;
  const words = lower.split(/\W+/);
  let matched = 0;
  for (const kw of rule.keywords) {
    if (words.includes(kw) || lower.includes(kw)) matched++;
  }
  if (matched === 0) return 0;
  return (matched / rule.keywords.length) * 80;
}

/**
 * Get a response for a user message.
 *
 * To integrate an AI API later:
 *   1. Make this function async
 *   2. Try the AI API first
 *   3. Fall back to rule-based if the API fails
 *
 * @param {string} message - User's message
 * @returns {{ text: string, suggestions: string[] }}
 */
export function getResponse(message) {
  if (!message || !message.trim()) return FALLBACK;

  let bestScore = 0;
  let bestRule = null;

  for (const rule of RULES) {
    const score = scoreRule(message.trim(), rule);
    if (score > bestScore) {
      bestScore = score;
      bestRule = rule;
    }
  }

  // Require a minimum confidence threshold
  if (bestScore >= 20 && bestRule) {
    return bestRule.response;
  }

  return FALLBACK;
}

/**
 * Get initial greeting when chat opens.
 */
export function getGreeting() {
  return {
    text: "Hi there! 👋 I'm the Maintenova assistant. I can help you learn about our platform, pricing, features, and more. What would you like to know?",
    suggestions: ['What does Maintenova do?', 'Show me pricing', 'How do I get started?'],
  };
}
